import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Collapse,
  IconButton,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { userRoute, STATES } from '../../constants.ts';
import { v4 as uuidv4 } from 'uuid';
import EditUserModal from './EditUserModal.tsx';
import ConfirmUserModal from './ConfirmUserModal.tsx';
import AddUserForm from './AddUserForm.tsx';
import UserTable from './UserTable/UserTable.tsx';

interface User {
  user_id: string;
  full_name: string;
  email: string;
  phone: string;
  password: string;
  is_admin: boolean;
  last_login: string;
  states: string[]; // stores state CODES (e.g., 'NC')
  custom_message?: string;
}

// Helper: map state code->name for display
const stateCodeToName = Object.fromEntries(STATES.map(({ code, name }) => [code, name]));

const UsersList: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [newUser, setNewUser] = useState<User>({
    user_id: '',
    full_name: '',
    email: '',
    phone: '',
    password: '',
    is_admin: false,
    last_login: new Date().toISOString(),
    states: [],
    custom_message: '',
  });
  const [formOpen, setFormOpen] = useState(false);

  // Actions menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuUserId, setMenuUserId] = useState<string | null>(null);

  // Edit modal state
  const [editOpen, setEditOpen] = useState(false);
  const [editOriginal, setEditOriginal] = useState<User | null>(null);
  const [editUser, setEditUser] = useState<User | null>(null);

  // Confirm modal state (for saving changes)
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [changesSummary, setChangesSummary] = useState<{ field: string; before: any; after: any }[]>([]);

  // Fetch users from the backend
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(userRoute);
        setUsers(response.data);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    fetchUsers();
  }, []);

  // Add a new user
  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!newUser.full_name || !newUser.email || !newUser.password || newUser.is_admin === null) {
        alert('Please fill in all fields.');
        return;
      }
      const userToCreate = {
        ...newUser,
        user_id: uuidv4(),
        email: newUser.email.toLowerCase(),
        states: newUser.states, // codes
        phone: newUser.phone,
      };
      const response = await axios.post(`${userRoute}/create`, userToCreate);
      setUsers([...users, response.data]);
      setNewUser({
        user_id: '',
        full_name: '',
        phone: '',
        email: '',
        password: '',
        is_admin: false,
        last_login: new Date().toISOString(),
        states: [],
        custom_message: '',
      });
    } catch (error) {
      console.error('Error adding user:', error);
    }
  };

  // Update a user (server call)
  const handleUpdateUser = async (id: string, updatedUser: Partial<User>) => {
    try {
      const response = await axios.put(`${userRoute}/${id}`, updatedUser);
      setUsers(users.map((user) => (user.user_id === id ? response.data : user)));
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  // Delete a user
  const handleDeleteUser = async (id: string) => {
    try {
      await axios.delete(`${userRoute}/delete/${id}`);
      setUsers(users.filter((user) => user.user_id !== id));
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  // Actions menu handlers
  const openMenu = (evt: React.MouseEvent<HTMLElement>, userId: string) => {
    setMenuAnchorEl(evt.currentTarget);
    setMenuUserId(userId);
  };
  const closeMenu = () => {
    setMenuAnchorEl(null);
    setMenuUserId(null);
  };

  // Open edit modal for a given user
  const openEdit = (user: User) => {
    setEditOriginal(user);
    setEditUser({ ...user });
    setEditOpen(true);
  };
  const closeEdit = () => {
    setEditOpen(false);
    setEditOriginal(null);
    setEditUser(null);
  };

  // Compute changes summary (diff)
  const computeDiff = (before: User, after: User) => {
    const keys: (keyof User)[] = ['full_name','email','phone','password','is_admin','states','custom_message'];
    const changes: { field: string; before: any; after: any }[] = [];
    for (const k of keys) {
      const b = before[k];
      const a = after[k];
      const eq = Array.isArray(b) && Array.isArray(a) ? JSON.stringify(b) === JSON.stringify(a) : b === a;
      if (!eq) {
        changes.push({ field: String(k), before: b, after: a });
      }
    }
    return changes;
  };

  // Save from edit modal → open confirm dialog
  const requestSave = () => {
    if (!editOriginal || !editUser) return;
    const changes = computeDiff(editOriginal, editUser);
    if (changes.length === 0) {
      alert('No changes to save.');
      return;
    }
    setChangesSummary(changes);
    setConfirmOpen(true);
  };

  // Confirm and send update to backend
  const confirmSave = async () => {
    if (!editOriginal || !editUser) return;
    const diff: Partial<User> = {};
    for (const c of changesSummary) {
      (diff as any)[c.field] = c.after;
    }
    await handleUpdateUser(editOriginal.user_id, diff);
    setConfirmOpen(false);
    closeEdit();
  };

  const cancelConfirm = () => setConfirmOpen(false);

  // Derived helper: render user states as names
  const renderStates = (codes: string[]) =>
    codes.map((c) => stateCodeToName[c] || c).join(', ');

  return (
    <Box>
      {/* Add User Form */}
      <Card sx={{ mb: 4 }}>
        <CardHeader
          avatar={<PersonAddIcon color="primary" />}
          title={<Typography variant="h6">Add New User</Typography>}
          action={
            <IconButton onClick={() => setFormOpen(!formOpen)}>
              {formOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          }
        />
        <Collapse in={formOpen}>
          <CardContent>
            <AddUserForm newUser={newUser} setNewUser={setNewUser} handleAddUser={handleAddUser} />
          </CardContent>
        </Collapse>
      </Card>

      {/* Users Table */}
      <UserTable users={users} openMenu={openMenu} renderStates={renderStates} menuAnchorEl={menuAnchorEl} closeMenu={closeMenu} openEdit={openEdit} handleDeleteUser={handleDeleteUser} menuUserId={menuUserId}/>

      {/* Edit User Modal */}
      <EditUserModal 
        closeEdit={closeEdit} 
        editOpen={editOpen} 
        editUser={editUser || null} 
        setEditUser={setEditUser} 
        requestSave={requestSave}
      />

      {/* Confirm Changes Modal */}
      <ConfirmUserModal 
        confirmOpen={confirmOpen} 
        cancelConfirm={cancelConfirm} 
        changesSummary={changesSummary} 
        renderStates={renderStates}
        confirmSave={confirmSave}
      />
    </Box>
  );
};

export default UsersList;